import type { FormFieldLogic, LogicCondition } from "./enums"
import { z } from "zod"
import { getBlockNotePlainText, parseBlockNoteContent } from "./editor-content"

export type FormFieldLike = {
	id: string
	type: string
	label: string
	description: string
	content: string
	required: boolean
	defaultAnswer?: string | number | boolean | string[]
	pageIndex?: number
	pageLogic?: FormFieldLogic
	options: {
		choices?: string[]
		allowOther?: boolean
		includesTime?: boolean
		includesDate?: boolean
		min?: number
		max?: number
	} | null
	logic: FormFieldLogic
}

export type FormAnswerLike = { fieldId: string; value: unknown }

// These root paths are required by authentication, APIs, and static assets.
export const formShareReservedPaths = [
	"api",
	"login",
	"logout",
	"native-auth",
	"auth-proxy",
	"assets",
	"public"
]

export const formLogicSchema = z.object({
	action: z.enum(["show", "hide"]),
	conditions: z
		.array(
			z.object({
				fieldId: z.string(),
				operator: z.enum([
					"isEmpty",
					"isNotEmpty",
					"equals",
					"notEquals",
					"contains",
					"notContains"
				]),
				value: z.string().optional()
			})
		)
		.max(100)
})

export const parseFormLogic = (raw: unknown): FormFieldLogic => {
	if (typeof raw !== "string" || !raw) return null
	try {
		const parsed = formLogicSchema.safeParse(JSON.parse(raw))
		return parsed.success ? parsed.data : null
	} catch {
		return null
	}
}

export const formQuestionTypes = [
	"shortText",
	"longText",
	"number",
	"email",
	"phone",
	"url",
	"signature",
	"dropdown",
	"checkbox",
	"checkboxGroup",
	"radioGroup",
	"date",
	"fileUpload"
] as const

export const isFormQuestion = (type: string) =>
	(formQuestionTypes as readonly string[]).includes(type)

export const answerText = (value: unknown, detailed = false): string => {
	if (Array.isArray(value)) {
		return value.map((item) => answerText(item, detailed)).join(", ")
	}
	if (typeof value === "object" && value !== null) {
		if (
			"choice" in value &&
			value.choice === "Other" &&
			"text" in value &&
			typeof value.text === "string"
		)
			return detailed ? `Other: ${value.text}` : value.text
		const file = value as { name?: unknown; url?: unknown }
		if (detailed && typeof file.url === "string")
			return typeof file.name === "string"
				? `${file.name} (${file.url})`
				: file.url
		return typeof file.name === "string" ? file.name : ""
	}
	if (typeof value === "boolean") return value ? "Yes" : "No"
	if (value === null || value === undefined) return ""
	return String(value)
}

const conditionMatches = (answerValue: unknown, condition: LogicCondition) => {
	if (
		answerValue &&
		typeof answerValue === "object" &&
		"choice" in answerValue &&
		answerValue.choice === "Other"
	)
		answerValue = "Other"
	const empty = Array.isArray(answerValue)
		? answerValue.length === 0
		: answerValue === null ||
			answerValue === undefined ||
			answerValue === "" ||
			typeof answerValue === "object"
	switch (condition.operator) {
		case "isEmpty":
			return empty
		case "isNotEmpty":
			return !empty
		case "equals":
			return answerText(answerValue) === (condition.value ?? "")
		case "notEquals":
			return answerText(answerValue) !== (condition.value ?? "")
		case "contains":
			if (Array.isArray(answerValue))
				return answerValue.includes(condition.value ?? "")
			return answerText(answerValue)
				.toLowerCase()
				.includes((condition.value ?? "").toLowerCase())
		case "notContains":
			if (Array.isArray(answerValue))
				return !answerValue.includes(condition.value ?? "")
			return !answerText(answerValue)
				.toLowerCase()
				.includes((condition.value ?? "").toLowerCase())
	}
}

const isFormLogicVisible = (
	logic: FormFieldLogic,
	answers: FormAnswerLike[]
) => {
	if (!logic) return true
	const matched =
		logic.conditions.length > 0 &&
		logic.conditions.every((condition) =>
			conditionMatches(
				answers.find((answer) => answer.fieldId === condition.fieldId)?.value,
				condition
			)
		)
	return logic.action === "hide" ? !matched : matched
}

export const isFieldVisible = (
	field: FormFieldLike,
	answers: FormAnswerLike[]
) => {
	if (!field.logic || !isFormQuestion(field.type)) return true
	return isFormLogicVisible(field.logic, answers)
}

export const visibleQuestionFields = <T extends FormFieldLike>(
	fields: T[],
	answers: FormAnswerLike[]
) => {
	const visible: T[] = []
	const precedingAnswers: FormAnswerLike[] = []
	let pageIndex = -1
	let pageVisible = true
	for (const field of fields) {
		if ((field.pageIndex ?? 0) !== pageIndex) {
			pageIndex = field.pageIndex ?? 0
			pageVisible = isFormLogicVisible(
				field.pageLogic ?? null,
				precedingAnswers
			)
		}
		if (!pageVisible || !isFieldVisible(field, precedingAnswers)) continue
		visible.push(field)
		const answer = answers.find((entry) => entry.fieldId === field.id)
		if (answer) precedingAnswers.push(answer)
	}
	return visible
}

/** Replaces `{{<fieldId>}}` tokens with earlier answers; unresolved tokens are dropped. */
export const renderPiping = (
	text: string,
	fields: FormFieldLike[],
	answers: FormAnswerLike[]
) =>
	text.replace(/\{\{([a-zA-Z0-9-]{1,64})\}\}/g, (_match, fieldId: string) => {
		const source = visibleQuestionFields(fields, answers).find(
			(field) => field.id === fieldId
		)
		if (!source) return ""
		return answerText(
			answers.find((answer) => answer.fieldId === fieldId)?.value
		)
	})

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phonePattern = /^[+0-9 ().-]{5,32}$/

export const formAnswersWithDefaults = (
	fields: FormFieldLike[],
	answers: FormAnswerLike[]
) => [
	...fields.flatMap((field) =>
		field.defaultAnswer !== undefined &&
		!answers.some((answer) => answer.fieldId === field.id)
			? [{ fieldId: field.id, value: field.defaultAnswer }]
			: []
	),
	...answers
]

export const validateFormAnswers = (
	fields: FormFieldLike[],
	inputAnswers: FormAnswerLike[],
	{ includeHidden = false } = {}
) => {
	const answers = formAnswersWithDefaults(fields, inputAnswers)
	const errors: Record<string, string> = {}
	if (new Set(answers.map((answer) => answer.fieldId)).size !== answers.length)
		errors.form = "Each question can only have one answer."
	const byFieldId = new Map(fields.map((field) => [field.id, field]))
	const resolved = visibleQuestionFields(fields, answers)
	for (const answer of answers) {
		const field = byFieldId.get(answer.fieldId)
		if (!field)
			errors.form = "This form changed. Refresh and try submitting again."
	}
	for (const field of resolved) {
		const value = answers.find((answer) => answer.fieldId === field.id)?.value
		const empty =
			value === null ||
			value === undefined ||
			value === "" ||
			(Array.isArray(value) && value.length === 0)
		if (
			field.required &&
			(empty || (field.type === "checkbox" && value !== true))
		) {
			errors[field.id] = "This field is required"
			continue
		}
		if (empty) continue
		switch (field.type) {
			case "shortText":
			case "longText": {
				if (typeof value !== "string") errors[field.id] = "Enter text"
				else if (
					field.options?.min !== undefined &&
					value.length < field.options.min
				)
					errors[field.id] = `Enter at least ${field.options.min} characters`
				else if (
					field.options?.max !== undefined &&
					value.length > field.options.max
				)
					errors[field.id] = `Use no more than ${field.options.max} characters`
				break
			}
			case "checkbox":
				if (typeof value !== "boolean")
					errors[field.id] = "Choose a checkbox value"
				break
			case "checkboxGroup": {
				const choices = field.options?.choices ?? []
				if (
					!Array.isArray(value) ||
					value.some(
						(choice) => typeof choice !== "string" || !choices.includes(choice)
					) ||
					new Set(value).size !== value.length
				)
					errors[field.id] = "Choose valid, unique options"
				else if (
					field.options?.min !== undefined &&
					value.length < field.options.min
				)
					errors[field.id] = `Choose at least ${field.options.min} options`
				else if (
					field.options?.max !== undefined &&
					value.length > field.options.max
				)
					errors[field.id] = `Choose no more than ${field.options.max} options`
				break
			}
			case "number": {
				const numeric = Number(value)
				if (typeof value !== "number" || !Number.isFinite(numeric))
					errors[field.id] = "Enter a number"
				else if (
					(field.options?.min !== undefined && numeric < field.options.min) ||
					(field.options?.max !== undefined && numeric > field.options.max)
				)
					errors[field.id] =
						`Enter a number between ${field.options?.min ?? "any"} and ${field.options?.max ?? "any"}`
				break
			}
			case "email":
				if (typeof value !== "string" || !emailPattern.test(value))
					errors[field.id] = "Enter a valid email address"
				break
			case "phone":
				if (typeof value !== "string" || !phonePattern.test(value.trim()))
					errors[field.id] = "Enter a valid phone number"
				break
			case "url":
				try {
					if (typeof value !== "string") throw new Error("Expected a URL")
					const parsed = new URL(value)
					if (!/^https?:$/.test(parsed.protocol))
						errors[field.id] = "Enter a valid URL"
				} catch {
					errors[field.id] = "Enter a valid URL"
				}
				break
			case "dropdown":
			case "radioGroup": {
				const other =
					field.options?.allowOther &&
					(value === "Other" ||
						(typeof value === "object" &&
							value !== null &&
							"choice" in value &&
							value.choice === "Other"))
				if (other) {
					if (
						typeof value !== "object" ||
						value === null ||
						!("text" in value) ||
						typeof value.text !== "string" ||
						!value.text.trim()
					)
						errors[field.id] = "Enter your other answer"
				} else if (
					typeof value !== "string" ||
					!(field.options?.choices ?? []).includes(value)
				) {
					errors[field.id] = "Choose one of the options"
				}
				break
			}
			case "date": {
				if (field.options?.includesDate === false) {
					if (
						typeof value !== "string" ||
						!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)
					)
						errors[field.id] = "Enter a valid time"
				} else {
					const pattern = field.options?.includesTime
						? /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/
						: /^\d{4}-\d{2}-\d{2}$/
					const date =
						typeof value === "string"
							? new Date(`${value.slice(0, 10)}T00:00:00Z`)
							: null
					if (
						typeof value !== "string" ||
						!pattern.test(value) ||
						!date ||
						Number.isNaN(date.getTime()) ||
						date.getUTCFullYear() < 1 ||
						date.toISOString().slice(0, 10) !== value.slice(0, 10)
					)
						errors[field.id] = "Enter a valid date"
				}
				break
			}
			case "fileUpload":
			case "signature": {
				const file = value as { name?: unknown; url?: unknown } | string
				const url = typeof file === "string" ? file : file?.url
				if (typeof url !== "string" || !/^https?:\/\//.test(url))
					errors[field.id] = "Upload a file"
				break
			}
		}
	}
	return {
		ok: Object.keys(errors).length === 0,
		errors,
		answers: answers.filter((answer) =>
			resolved.some((field) => field.id === answer.fieldId)
		),
		visible: includeHidden ? fields : resolved
	}
}

/** Page breaks divide top-level blocks; nested questions stay on their parent's page. */
const formPageDefinitionsFromContent = (content: string | null | undefined) => {
	const pages: { questionIds: string[]; logic: FormFieldLogic }[] = [
		{ questionIds: [], logic: null }
	]
	for (const block of parseBlockNoteContent(content)?.blocks ?? []) {
		const blocks = [block]
		if (block.type === "formPageBreak") {
			pages.push({ questionIds: [], logic: parseFormLogic(block.props.logic) })
			continue
		}
		for (let index = 0; index < blocks.length; index++) {
			const current = blocks[index]!
			if (current.type === "question")
				pages[pages.length - 1]!.questionIds.push(current.id)
			if (current.children?.length) blocks.push(...current.children)
		}
	}
	return pages
}

export const formPagesFromContent = (content: string | null | undefined) =>
	formPageDefinitionsFromContent(content).map((page) => page.questionIds)

/** Only earlier, visible answers may decide whether an entire page is visited. */
export const visibleFormPages = (
	content: string | null | undefined,
	inputAnswers: FormAnswerLike[]
) => {
	const fields = formQuestionsFromContent(content)
	const answers = formAnswersWithDefaults(fields, inputAnswers)
	const visible = visibleQuestionFields(fields, answers)
	return formPageDefinitionsFromContent(content)
		.map((page, index) => ({ ...page, index }))
		.filter((page) =>
			isFormLogicVisible(
				page.logic,
				answers.filter((answer) =>
					visible.some(
						(field) =>
							field.id === answer.fieldId && (field.pageIndex ?? 0) < page.index
					)
				)
			)
		)
}

/** Extracts answerable questions from a form's BlockNote envelope (client and server safe). */
export const formQuestionsFromContent = (content: unknown): FormFieldLike[] => {
	const parsed = parseBlockNoteContent(
		typeof content === "string" ? content : null
	)
	if (!parsed) return []
	const questionPages = new Map(
		formPageDefinitionsFromContent(
			typeof content === "string" ? content : null
		).flatMap((page, pageIndex) =>
			page.questionIds.map(
				(id) => [id, { pageIndex, pageLogic: page.logic }] as const
			)
		)
	)
	const questionBlocks = [...parsed.blocks]
	for (let index = 0; index < questionBlocks.length; index++) {
		const children = questionBlocks[index]?.children
		if (children?.length) questionBlocks.splice(index + 1, 0, ...children)
	}
	return questionBlocks
		.filter((block) => block.type === "question")
		.map((block): FormFieldLike => {
			const props = block.props
			return {
				id: block.id,
				...questionPages.get(block.id),
				type: String(props.qtype ?? "shortText"),
				label: getBlockNotePlainText([{ ...block, children: [] }]),
				description: String(props.description ?? ""),
				content: "",
				required: props.required === true,
				defaultAnswer:
					props.defaultAnswerEnabled === true
						? props.qtype === "checkboxGroup"
							? String(props.defaultAnswer ?? "")
									.split("\n")
									.filter(Boolean)
							: props.qtype === "checkbox"
								? props.defaultAnswer === "true"
								: props.defaultAnswer !== "" && props.qtype === "number"
									? Number(props.defaultAnswer)
									: String(props.defaultAnswer ?? "")
						: undefined,
				options: {
					allowOther: props.allowOther === true,
					choices:
						typeof props.choices === "string"
							? props.choices.split("\n").filter(Boolean)
							: [],
					includesTime: props.includesTime === true,
					includesDate: props.includesDate !== false,
					min:
						props.min !== "" && props.min !== undefined
							? Number(props.min)
							: undefined,
					max:
						props.max !== "" && props.max !== undefined
							? Number(props.max)
							: undefined
				},
				logic: parseFormLogic(props.logic)
			}
		})
}
